import React, { useEffect, useRef, useState } from 'react';
import { collection, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { ArrowIcon, SubPageFoot, SubPageNav } from '../components/SubPageChrome';
import {
  CoachingExperienceLevel,
  CoachingInquiryDoc,
  CoachingPreferredTime,
  COLLECTION_COACHING_INQUIRIES,
} from '../constants/firestore';
import { trackEvent } from '../utils/analytics';
import { usePageMeta } from '../utils/usePageMeta';
import drewPhoto from '../assets/drew-coach.webp';
import heroPhoto from '../assets/tempo_player1.webp';
import './HomePage.css';
import './CoachingPage.css';

interface FormValues {
  contactName: string;
  email: string;
  phone: string;
  experienceLevel: '' | CoachingExperienceLevel;
  lessonGoals: string;
  preferredDays: string;
  preferredTime: '' | CoachingPreferredTime;
  message: string;
}

const EMPTY_FORM: FormValues = {
  contactName: '',
  email: '',
  phone: '',
  experienceLevel: '',
  lessonGoals: '',
  preferredDays: '',
  preferredTime: '',
  message: '',
};

const FIELD_IDS: Partial<Record<keyof FormValues, string>> = {
  contactName: 'coaching-name',
  email: 'coaching-email',
  phone: 'coaching-phone',
  experienceLevel: 'coaching-experience',
  lessonGoals: 'coaching-goals',
  preferredDays: 'coaching-days',
  preferredTime: 'coaching-time',
  message: 'coaching-message',
};

const coachingBenefits = [
  {
    index: '01',
    title: 'Start strong.',
    body: 'Build sound fundamentals from day one, from court positioning and shot selection to clean, repeatable technique.',
  },
  {
    index: '02',
    title: 'Sharpen your game.',
    body: 'Turn recurring mistakes into focused practice with feedback tailored to how you move, learn, and compete.',
  },
  {
    index: '03',
    title: 'Play smarter.',
    body: 'Learn patterns, positioning, and decision-making that translate from the lesson directly into your next match.',
  },
];

function getAttribution() {
  const params = new URLSearchParams(window.location.search);
  const limited = (value: string | null, maxLength: number) =>
    value ? value.slice(0, maxLength) : null;
  return {
    utm_source: limited(params.get('utm_source'), 200),
    utm_medium: limited(params.get('utm_medium'), 200),
    utm_campaign: limited(params.get('utm_campaign'), 200),
    utm_content: limited(params.get('utm_content'), 200),
    utm_term: limited(params.get('utm_term'), 200),
    fbclid: limited(params.get('fbclid'), 500),
    gclid: limited(params.get('gclid'), 500),
  };
}

function getDeviceType() {
  return window.innerWidth <= 768 ? 'mobile' : window.innerWidth <= 1024 ? 'tablet' : 'desktop';
}

const CoachingPage: React.FC = () => {
  const [values, setValues] = useState<FormValues>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const formStartedRef = useRef(false);

  usePageMeta({
    title: 'Private Padel Coaching in Houston | TEMPO',
    description:
      'Book private padel coaching at TEMPO in Houston. Personalized lessons for beginners and experienced players focused on technique, strategy, confidence, and match play.',
    canonicalPath: '/coaching',
  });

  useEffect(() => {
    trackEvent('page_view', { page: 'coaching' });
  }, []);

  const scrollToForm = () => {
    trackEvent('coaching_cta_click', { destination: 'lesson_inquiry' });
    document.getElementById('lesson-inquiry')?.scrollIntoView({ behavior: 'smooth' });
  };

  const setField = (field: keyof FormValues, value: string) => {
    if (!formStartedRef.current) {
      formStartedRef.current = true;
      trackEvent('form_start', { form: 'coaching_inquiry' });
    }
    setValues((current) => ({ ...current, [field]: value }));
    if (errors[field]) {
      setErrors((current) => {
        const next = { ...current };
        delete next[field];
        delete next.submit;
        return next;
      });
    }
  };

  const validate = () => {
    const next: Record<string, string> = {};
    const contactName = values.contactName.trim();
    const email = values.email.trim();
    const lessonGoals = values.lessonGoals.trim();
    const preferredDays = values.preferredDays.trim();
    const message = values.message.trim();
    if (!contactName) {
      next.contactName = 'Full name is required';
    } else if (contactName.length < 2) {
      next.contactName = 'Full name must be at least 2 characters';
    } else if (contactName.length > 120) {
      next.contactName = 'Full name must be 120 characters or less';
    }
    if (!email) {
      next.email = 'Email is required';
    } else if (email.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      next.email = 'Please enter a valid email';
    }
    const phone = values.phone.trim();
    const phoneDigitCount = phone.replace(/\D/g, '').length;
    if (!phone) {
      next.phone = 'Phone is required';
    } else if (
      phone.length > 30
      || !/^[\d\s()+-]+$/.test(phone)
      || phoneDigitCount < 7
      || phoneDigitCount > 15
    ) {
      next.phone = 'Please enter a valid phone number';
    }
    if (!values.experienceLevel) next.experienceLevel = 'Experience level is required';
    if (!lessonGoals) {
      next.lessonGoals = 'Lesson goals are required';
    } else if (lessonGoals.length < 3) {
      next.lessonGoals = 'Lesson goals must be at least 3 characters';
    } else if (lessonGoals.length > 2000) {
      next.lessonGoals = 'Lesson goals must be 2,000 characters or less';
    }
    if (!preferredDays) {
      next.preferredDays = 'Preferred days are required';
    } else if (preferredDays.length < 2) {
      next.preferredDays = 'Preferred days must be at least 2 characters';
    } else if (preferredDays.length > 500) {
      next.preferredDays = 'Preferred days must be 500 characters or less';
    }
    if (!values.preferredTime) next.preferredTime = 'Preferred time is required';
    if (message.length > 2000) next.message = 'Additional notes must be 2,000 characters or less';
    if (Object.keys(next).length) next.submit = 'Please complete the required fields above.';
    setErrors(next);
    const firstInvalidField = Object.keys(next).find((field) => field !== 'submit') as keyof FormValues | undefined;
    if (firstInvalidField) {
      document.getElementById(FIELD_IDS[firstInvalidField] || '')?.focus();
    }
    return Object.keys(next).length === 0;
  };

  const submitInquiry = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);

    try {
      const attribution = getAttribution();
      const payload: CoachingInquiryDoc = {
        doc_type: 'coaching_inquiry_v1',
        coach: 'Drew',
        contact_name: values.contactName.trim(),
        email: values.email.trim().toLowerCase(),
        phone: values.phone.trim(),
        experience_level: values.experienceLevel as CoachingExperienceLevel,
        lesson_goals: values.lessonGoals.trim(),
        preferred_days: values.preferredDays.trim(),
        preferred_time: values.preferredTime as CoachingPreferredTime,
        message: values.message.trim() || null,
        ...attribution,
        referrer: document.referrer ? document.referrer.slice(0, 2048) : null,
        device_type: getDeviceType(),
        created_at: serverTimestamp(),
        source: 'coaching_page',
        status: 'new',
      };

      const ref = doc(collection(db, COLLECTION_COACHING_INQUIRIES));
      await setDoc(ref, payload);
      trackEvent('coaching_inquiry_submit', {
        coach: 'Drew',
        experience_level: payload.experience_level,
        ...attribution,
      });
      setValues(EMPTY_FORM);
      setErrors({});
      setIsSuccess(true);
    } catch (error) {
      console.error('Coaching inquiry error:', error);
      setErrors({ submit: 'Something went wrong. Please call us or try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="home-v2 coaching-page">
      <SubPageNav
        ctaLabel="Book a Lesson"
        ctaHref="#lesson-inquiry"
        onCtaClick={scrollToForm}
        active="coaching"
      />

      <section className="coaching-hero" aria-labelledby="coaching-hero-title">
        <div
          className="coaching-hero-image"
          role="img"
          aria-label="Padel player training at Tempo"
          style={{ backgroundImage: `url(${heroPhoto})` }}
        />
        <div className="coaching-hero-shade" />
        <div className="v2-container coaching-hero-content">
          <span className="coaching-kicker">Private lessons at Tempo</span>
          <h1 id="coaching-hero-title">
            <span>Private</span>
            <em>Coaching.</em>
          </h1>
          <p>
            One-on-one instruction built around your game—whether you are picking up a racket
            for the first time or looking for the details that change a close match.
          </p>
          <button type="button" className="v2-btn v2-btn-primary" onClick={scrollToForm}>
            Book Your First Lesson
            <ArrowIcon />
          </button>
        </div>
      </section>

      <section className="v2-section noborder" aria-labelledby="coaching-benefits-title">
        <div className="v2-container">
          <div className="v2-sec-head">
            <div className="left">
              <span className="num">01 /</span>
              <span className="label">Why coaching</span>
            </div>
            <h2 id="coaching-benefits-title">
              Build your game, <em>one detail</em> at a time.
            </h2>
          </div>
          <div className="coaching-benefits">
            {coachingBenefits.map((benefit) => (
              <article className="coaching-benefit" key={benefit.index}>
                <span>{benefit.index}</span>
                <h3>{benefit.title}</h3>
                <p>{benefit.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="v2-section coaching-fit-section" aria-labelledby="coaching-fit-title">
        <div className="v2-container coaching-fit-grid">
          <div>
            <span className="coaching-kicker">Lessons for every level</span>
            <h2 id="coaching-fit-title">
              <span>Your lesson.</span>
              <em>Your pace.</em>
            </h2>
          </div>
          <div className="coaching-fit-copy">
            <p>
              New players can learn the game with clear fundamentals and confidence. Experienced
              players can target technique, tactics, movement, and match-specific goals.
            </p>
            <ul>
              <li>First-time and beginner fundamentals</li>
              <li>Technique and shot development</li>
              <li>Court positioning and match strategy</li>
              <li>Personalized drills and practical feedback</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="v2-section coaching-coach-section" aria-labelledby="drew-title">
        <div className="v2-container">
          <div className="coaching-coach-grid">
            <div className="coaching-coach-photo">
              <img src={drewPhoto} alt="Drew, Tempo padel coach" />
              <span>Tempo coaching / Houston, Texas</span>
            </div>
            <div className="coaching-coach-copy">
              <span className="coaching-kicker">Meet your coach</span>
              <h2 id="drew-title">
                Meet <em>Drew.</em>
              </h2>
              <p>
                Houston native with a lifelong background in competitive tennis and high level
                coaching. Now passionate about padel, I bring a technical yet fun approach to help
                players develop their skills, confidence, and love for the game.
              </p>
              <button type="button" className="v2-btn v2-btn-on-light" onClick={scrollToForm}>
                Request a Lesson
                <ArrowIcon />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="v2-section coaching-form-section" id="lesson-inquiry" aria-labelledby="lesson-form-title">
        <div className="v2-container coaching-form-layout">
          <div className="coaching-form-intro">
            <span className="coaching-kicker">Get started</span>
            <h2 id="lesson-form-title">
              <span>Book your</span>
              <span>first <em>lesson.</em></span>
            </h2>
            <p>
              Tell us where you are in your game and when you would like to play. The Tempo team
              will follow up to confirm the best available lesson time.
            </p>
            <div className="coaching-form-note">
              <strong>Prefer to talk?</strong>
              <a href="tel:+17135706716">+1 713 570 6716</a>
            </div>
          </div>

          {isSuccess ? (
            <div className="coaching-success" role="status">
              <span className="coaching-success-mark" aria-hidden="true">✓</span>
              <h2>We'll be in touch.</h2>
              <p>
                Thanks for reaching out. Our team will review your lesson request and contact you
                with scheduling options.
              </p>
              <button type="button" className="v2-btn v2-btn-on-light" onClick={() => setIsSuccess(false)}>
                Send Another Inquiry
              </button>
            </div>
          ) : (
            <form className="coaching-form" onSubmit={submitInquiry} noValidate>
              <div className="v2-form-grid">
                <div className="v2-form-row full">
                  <label htmlFor="coaching-name">Full name *</label>
                  <input
                    id="coaching-name"
                    value={values.contactName}
                    maxLength={120}
                    onChange={(event) => setField('contactName', event.target.value)}
                    aria-invalid={Boolean(errors.contactName)}
                    aria-describedby={errors.contactName ? 'coaching-name-error' : undefined}
                  />
                  {errors.contactName && <span className="err" id="coaching-name-error">{errors.contactName}</span>}
                </div>
                <div className="v2-form-row">
                  <label htmlFor="coaching-email">Email *</label>
                  <input
                    id="coaching-email"
                    type="email"
                    value={values.email}
                    maxLength={254}
                    onChange={(event) => setField('email', event.target.value)}
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={errors.email ? 'coaching-email-error' : undefined}
                  />
                  {errors.email && <span className="err" id="coaching-email-error">{errors.email}</span>}
                </div>
                <div className="v2-form-row">
                  <label htmlFor="coaching-phone">Phone *</label>
                  <input
                    id="coaching-phone"
                    type="tel"
                    value={values.phone}
                    maxLength={30}
                    onChange={(event) => setField('phone', event.target.value)}
                    aria-invalid={Boolean(errors.phone)}
                    aria-describedby={errors.phone ? 'coaching-phone-error' : undefined}
                  />
                  {errors.phone && <span className="err" id="coaching-phone-error">{errors.phone}</span>}
                </div>
                <div className="v2-form-row">
                  <label htmlFor="coaching-experience">Experience level *</label>
                  <select
                    id="coaching-experience"
                    value={values.experienceLevel}
                    onChange={(event) => setField('experienceLevel', event.target.value)}
                    aria-invalid={Boolean(errors.experienceLevel)}
                    aria-describedby={errors.experienceLevel ? 'coaching-experience-error' : undefined}
                  >
                    <option value="">Select your level</option>
                    <option value="new_to_padel">New to padel</option>
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                  {errors.experienceLevel && <span className="err" id="coaching-experience-error">{errors.experienceLevel}</span>}
                </div>
                <div className="v2-form-row">
                  <label htmlFor="coaching-time">Preferred time *</label>
                  <select
                    id="coaching-time"
                    value={values.preferredTime}
                    onChange={(event) => setField('preferredTime', event.target.value)}
                    aria-invalid={Boolean(errors.preferredTime)}
                    aria-describedby={errors.preferredTime ? 'coaching-time-error' : undefined}
                  >
                    <option value="">Select a time</option>
                    <option value="morning">Morning</option>
                    <option value="afternoon">Afternoon</option>
                    <option value="evening">Evening</option>
                    <option value="flexible">Flexible</option>
                  </select>
                  {errors.preferredTime && <span className="err" id="coaching-time-error">{errors.preferredTime}</span>}
                </div>
                <div className="v2-form-row full">
                  <label htmlFor="coaching-goals">Lesson goals *</label>
                  <textarea
                    id="coaching-goals"
                    rows={4}
                    value={values.lessonGoals}
                    maxLength={2000}
                    onChange={(event) => setField('lessonGoals', event.target.value)}
                    placeholder="What would you like to improve?"
                    aria-invalid={Boolean(errors.lessonGoals)}
                    aria-describedby={errors.lessonGoals ? 'coaching-goals-error' : undefined}
                  />
                  {errors.lessonGoals && <span className="err" id="coaching-goals-error">{errors.lessonGoals}</span>}
                </div>
                <div className="v2-form-row full">
                  <label htmlFor="coaching-days">Preferred days *</label>
                  <input
                    id="coaching-days"
                    value={values.preferredDays}
                    maxLength={500}
                    onChange={(event) => setField('preferredDays', event.target.value)}
                    placeholder="For example: Tuesdays or Thursday evenings"
                    aria-invalid={Boolean(errors.preferredDays)}
                    aria-describedby={errors.preferredDays ? 'coaching-days-error' : undefined}
                  />
                  {errors.preferredDays && <span className="err" id="coaching-days-error">{errors.preferredDays}</span>}
                </div>
                <div className="v2-form-row full">
                  <label htmlFor="coaching-message">Anything else?</label>
                  <textarea
                    id="coaching-message"
                    rows={3}
                    value={values.message}
                    maxLength={2000}
                    onChange={(event) => setField('message', event.target.value)}
                    placeholder="Questions, injuries, or anything else we should know"
                    aria-invalid={Boolean(errors.message)}
                    aria-describedby={errors.message ? 'coaching-message-error' : undefined}
                  />
                  {errors.message && <span className="err" id="coaching-message-error">{errors.message}</span>}
                </div>
              </div>
              {errors.submit && (
                <div className="v2-form-error-banner" role="alert" aria-live="assertive">
                  {errors.submit}
                </div>
              )}
              <button type="submit" className="v2-btn v2-btn-primary coaching-submit" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Lesson Inquiry'}
                {!isSubmitting && <ArrowIcon />}
              </button>
            </form>
          )}
        </div>
      </section>

      <SubPageFoot />

      <div className="v2-sticky-cta">
        <button type="button" className="v2-btn v2-btn-on-light" onClick={scrollToForm}>
          Book a Lesson
          <ArrowIcon />
        </button>
      </div>
    </div>
  );
};

export default CoachingPage;
